import { useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  formatDuration,
  lockHeadline,
  phaseElapsedMs,
  phaseProgressText,
} from './lockProgressFormat';

function StateIcon({ state }) {
  switch (state) {
    case 'done':
      return <CheckCircleIcon color="success" fontSize="small" />;
    case 'running':
      return <CircularProgress size={16} thickness={5} />;
    case 'failed':
      return <ErrorOutlineIcon color="error" fontSize="small" />;
    case 'skipped':
      return <RemoveCircleOutlineIcon color="disabled" fontSize="small" />;
    default:
      return <RadioButtonUncheckedIcon color="disabled" fontSize="small" />;
  }
}

const stateColour = state =>
  ({ done: 'success', running: 'info', failed: 'error', skipped: 'default' })[state] ?? 'default';

/**
 * "Lock progress" segment: an ordered list of the gameweek-lock phases with
 * their state, done/total counts, shard progress while the worker sets exist,
 * and elapsed time. Everything is derived server-side from persisted rows
 * (see gameweek-transition.phases.ts); this component only renders it.
 */
export default function LockProgress({ progress }) {
  // Re-render every second so running phases' elapsed time ticks between polls.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const running = progress?.deadlineCrossed && !progress?.complete;
    if (!running) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [progress?.deadlineCrossed, progress?.complete]);

  if (!progress?.phases?.length) return null;

  const currentIndex = progress.phases.findIndex(p => p.key === progress.currentPhase);
  const headlineColour =
    progress.phases[currentIndex]?.state === 'failed'
      ? 'error.main'
      : progress.complete
        ? 'success.main'
        : 'text.primary';

  return (
    <Box data-testid="lock-progress">
      <Typography variant="overline" color="text.secondary">
        Lock progress
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: headlineColour, mb: 1 }}>
        {lockHeadline(progress)}
      </Typography>

      <Stack spacing={0.75}>
        {progress.phases.map((phase, index) => {
          const isCurrent = index === currentIndex;
          const elapsed = formatDuration(phaseElapsedMs(phase, nowMs));
          const progressText = phaseProgressText(phase);
          return (
            <Stack
              key={phase.key}
              direction="row"
              spacing={1.5}
              alignItems="flex-start"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: isCurrent ? 'action.hover' : 'transparent',
              }}
            >
              <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', pt: 0.25 }}>
                <StateIcon state={phase.state} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isCurrent ? 700 : 500,
                      color: phase.state === 'pending' ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {index + 1}. {phase.label}
                  </Typography>
                  {phase.state !== 'pending' ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={stateColour(phase.state)}
                      label={phase.state}
                    />
                  ) : null}
                  {progressText ? (
                    <Typography variant="body2" color="text.secondary">
                      {progressText}
                    </Typography>
                  ) : null}
                  {elapsed ? (
                    <Typography variant="caption" color="text.secondary">
                      {phase.endedAt ? `took ${elapsed}` : `${elapsed} so far`}
                    </Typography>
                  ) : null}
                </Stack>
                {phase.detail ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: phase.state === 'failed' ? 'error.main' : 'text.secondary',
                    }}
                  >
                    {phase.detail}
                  </Typography>
                ) : null}
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
